import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import AdminNav from '../components/AdminNav'
import { useToast } from '../context/ToastContext'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'
const GREEN  = '#16A34A'
const AMBER  = '#D97706'
const RED    = '#DC2626'

const inp = {
  width: '100%', border: '1px solid ' + BORDER, borderRadius: '9px',
  padding: '8px 12px', fontSize: '12.5px', outline: 'none',
  background: WHITE, color: TEXT, fontFamily: 'Space Grotesk, system-ui, sans-serif',
  boxSizing: 'border-box',
}

const STATUS_CONFIG = {
  pending:   { label: 'Payment Pending', color: AMBER,  bg: '#FFFBEB' },
  paid:      { label: 'Being Prepared',  color: BLUE,   bg: '#EFF6FF' },
  preparing: { label: 'Being Prepared',  color: BLUE,   bg: '#EFF6FF' },
  running:   { label: 'Running',         color: GREEN,  bg: '#F0FDF4' },
  completed: { label: 'Completed',       color: MUTED,  bg: BG        },
  cancelled: { label: 'Cancelled',       color: RED,    bg: '#FEF2F2' },
}

const BOOST_TYPE_LABELS = {
  post: 'Post', video_reel: 'Video/Reel', live_session: 'Live',
  page: 'Page', product: 'Product', website: 'Link'
}

const GOAL_LABELS = {
  reach: 'Reach', engagement: 'Engagement', messages: 'Messages',
  page_likes: 'Page Likes', followers: 'Followers', video_views: 'Video Views',
  traffic: 'Traffic', conversions: 'Conversions'
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden', marginBottom: '16px' }}>
      {(title || subtitle) && (
        <div style={{ padding: '14px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
          {title && <div style={{ fontSize: '13px', fontWeight: 700, color: TEXT }}>{title}</div>}
          {subtitle && <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{subtitle}</div>}
        </div>
      )}
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  )
}

function BriefModal({ boost, onClose, onLaunch, onComplete, onCancel }) {
  const { showToast } = useToast()
  const [results, setResults] = useState({
    result_reach: '', result_impressions: '', result_clicks: '',
    result_engagements: '', result_spent_usd: '', result_outcomes: '',
    result_notes: '', fb_page_followers_end: '',
  })
  const [saving, setSaving] = useState(false)
  const [mode, setMode] = useState('brief') // brief | results | cancel

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    showToast('Copied!')
  }

  const handleLaunch = async () => {
    setSaving(true)
    const endsAt = new Date()
    endsAt.setDate(endsAt.getDate() + boost.duration_days)
    await supabase.from('boosts').update({
      status: 'running',
      launched_at: new Date().toISOString(),
      ends_at: endsAt.toISOString(),
    }).eq('id', boost.id)

    // Notification
    await supabase.from('boost_notifications').insert({
      boost_id: boost.id,
      seller_id: boost.seller_id,
      type: 'launched',
      message: `Your campaign "${boost.fb_campaign_name}" is now live! It runs for ${boost.duration_days} days.`,
    })

    showToast('Campaign marked as launched!')
    setSaving(false)
    onLaunch()
    onClose()
  }

  const handleComplete = async () => {
    setSaving(true)
    await supabase.from('boosts').update({
      status: 'completed',
      completed_at: new Date().toISOString(),
      result_reach: results.result_reach ? Number(results.result_reach) : null,
      result_impressions: results.result_impressions ? Number(results.result_impressions) : null,
      result_clicks: results.result_clicks ? Number(results.result_clicks) : null,
      result_engagements: results.result_engagements ? Number(results.result_engagements) : null,
      result_spent_usd: results.result_spent_usd ? Number(results.result_spent_usd) : null,
      result_outcomes: results.result_outcomes ? Number(results.result_outcomes) : null,
      result_notes: results.result_notes || null,
      fb_page_followers_end: results.fb_page_followers_end ? Number(results.fb_page_followers_end) : null,
    }).eq('id', boost.id)

    // Notification
    await supabase.from('boost_notifications').insert({
      boost_id: boost.id,
      seller_id: boost.seller_id,
      type: 'completed',
      message: `Your campaign "${boost.fb_campaign_name}" is complete! Check your results.`,
    })

    showToast('Campaign completed and results saved!')
    setSaving(false)
    onComplete()
    onClose()
  }

  const handleCancel = async () => {
    setSaving(true)
    await supabase.from('boosts').update({ status: 'cancelled' }).eq('id', boost.id)
    showToast('Campaign cancelled')
    setSaving(false)
    onCancel()
    onClose()
  }

  const isEnding = boost.ends_at && new Date(boost.ends_at).getTime() - Date.now() < 86400000 && boost.status === 'running'

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, borderRadius: '16px', width: '100%', maxWidth: '620px', maxHeight: '90vh', overflowY: 'auto', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: WHITE, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: TEXT }}>{boost.fb_campaign_name}</div>
            <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{boost.seller_name || 'Seller'} · {BOOST_TYPE_LABELS[boost.boost_type]} · {GOAL_LABELS[boost.goal]}</div>
          </div>
          <button onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '8px', background: BG, border: 'none', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '12px 20px', borderBottom: '1px solid ' + BORDER, background: BG }}>
          {[
            { key: 'brief', label: '📋 Campaign Brief' },
            { key: 'results', label: '📊 Enter Results' },
            { key: 'cancel', label: '❌ Cancel' },
          ].map(m => (
            <button key={m.key} onClick={() => setMode(m.key)}
              style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: mode === m.key ? BLUE : WHITE, color: mode === m.key ? WHITE : MUTED, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
              {m.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px' }}>

          {/* ── BRIEF MODE ── */}
          {mode === 'brief' && (
            <>
              {isEnding && (
                <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '12.5px', fontWeight: 700, color: RED }}>
                  🔴 This campaign ends today — mark as complete after it finishes.
                </div>
              )}

              <div style={{ background: BG, borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>Campaign Brief — Copy into Business Manager</div>

                {[
                  { label: 'Campaign Name', val: boost.fb_campaign_name, copy: true },
                  { label: 'Seller', val: boost.seller_name || '—' },
                  { label: 'Market', val: boost.market },
                  { label: 'Boost Type', val: BOOST_TYPE_LABELS[boost.boost_type] },
                  { label: 'Goal', val: GOAL_LABELS[boost.goal] },
                  { label: 'Facebook URL', val: boost.fb_url || (boost.boost_type === 'product' ? `Product: ${boost.product_name}` : '—'), copy: !!boost.fb_url },
                  { label: 'Ad Budget', val: boost.market === 'BD' ? `৳${boost.budget_bdt?.toLocaleString()} → $${boost.budget_usd?.toFixed(2)} USD` : `$${boost.budget_usd?.toFixed(2)} USD` },
                  { label: 'Duration', val: `${boost.duration_days} days` },
                  { label: 'Start Date', val: boost.scheduled_start_date || 'ASAP' },
                  { label: 'Audience', val: boost.audience_notes || (boost.market === 'BD' ? 'Bangladesh, All ages' : 'Based on seller location') },
                  { label: 'USD Rate Used', val: boost.usd_rate_used ? `৳${boost.usd_rate_used}/USD` : '—' },
                ].map(row => (
                  <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid ' + BORDER, gap: '10px' }}>
                    <span style={{ fontSize: '11.5px', color: MUTED, flexShrink: 0 }}>{row.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: TEXT, textAlign: 'right', wordBreak: 'break-all' }}>{row.val}</span>
                      {row.copy && (
                        <button onClick={() => copyToClipboard(row.val)}
                          style={{ padding: '2px 8px', borderRadius: '5px', background: '#EFF6FF', border: 'none', fontSize: '10px', fontWeight: 700, color: BLUE, cursor: 'pointer', flexShrink: 0, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                          Copy
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Notes */}
              {boost.admin_notes && (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: AMBER, marginBottom: '4px' }}>Admin Notes</div>
                  <div style={{ fontSize: '12px', color: TEXT }}>{boost.admin_notes}</div>
                </div>
              )}

              {/* Creative */}
              {boost.creative_url && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, marginBottom: '8px' }}>CREATIVE</div>
                  <img src={boost.creative_url} alt="Creative" style={{ maxWidth: '100%', borderRadius: '10px', border: '1px solid ' + BORDER }} />
                </div>
              )}
              {boost.creative_request && (
                <div style={{ background: BG, borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: MUTED, marginBottom: '4px' }}>CREATIVE REQUEST</div>
                  <div style={{ fontSize: '12px', color: TEXT }}>{boost.creative_request}</div>
                </div>
              )}

              {/* Launch button */}
              {(boost.status === 'paid' || boost.status === 'preparing') && (
                <button onClick={handleLaunch} disabled={saving}
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', background: saving ? BORDER : GREEN, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                  {saving ? 'Saving...' : '🚀 Mark as Launched'}
                </button>
              )}
            </>
          )}

          {/* ── RESULTS MODE ── */}
          {mode === 'results' && (
            <>
              <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#1E40AF' }}>
                Enter results from Facebook Business Manager. These will be shown to the seller immediately on save.
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                {[
                  { key: 'result_reach', label: 'Reach' },
                  { key: 'result_impressions', label: 'Impressions' },
                  { key: 'result_clicks', label: 'Link Clicks' },
                  { key: 'result_engagements', label: 'Engagements' },
                  { key: 'result_outcomes', label: 'Results / Outcomes' },
                  { key: 'result_spent_usd', label: 'Amount Spent (USD)' },
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ fontSize: '11px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>{field.label}</label>
                    <input type="number" value={results[field.key]} onChange={e => setResults(r => ({ ...r, [field.key]: e.target.value }))}
                      placeholder="0" style={{ ...inp }}
                      onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  </div>
                ))}
              </div>

              {boost.fb_page_followers_start && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>
                    Page Followers at End <span style={{ color: MUTED, fontWeight: 400 }}>(was {boost.fb_page_followers_start?.toLocaleString()} at start)</span>
                  </label>
                  <input type="number" value={results.fb_page_followers_end} onChange={e => setResults(r => ({ ...r, fb_page_followers_end: e.target.value }))}
                    placeholder="e.g. 4850" style={{ ...inp, maxWidth: '200px' }}
                    onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
                  {results.fb_page_followers_end && boost.fb_page_followers_start && (
                    <div style={{ fontSize: '11.5px', color: GREEN, marginTop: '4px', fontWeight: 600 }}>
                      +{(Number(results.fb_page_followers_end) - Number(boost.fb_page_followers_start)).toLocaleString()} new followers
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontSize: '11px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Notes (optional)</label>
                <textarea value={results.result_notes} onChange={e => setResults(r => ({ ...r, result_notes: e.target.value }))}
                  placeholder="Any additional notes about campaign performance..."
                  rows={3} style={{ ...inp, resize: 'vertical' }}
                  onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
              </div>

              <button onClick={handleComplete} disabled={saving}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', background: saving ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                {saving ? 'Saving...' : '✓ Save Results & Mark Complete'}
              </button>
            </>
          )}

          {/* ── CANCEL MODE ── */}
          {mode === 'cancel' && (
            <>
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '10px', padding: '16px', marginBottom: '16px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>⚠️</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: RED, marginBottom: '6px' }}>Cancel this campaign?</div>
                <div style={{ fontSize: '12px', color: MUTED }}>This will mark the campaign as cancelled. The seller will be notified. Make sure to process any refund separately.</div>
              </div>
              <button onClick={handleCancel} disabled={saving}
                style={{ width: '100%', padding: '12px', borderRadius: '10px', background: saving ? BORDER : RED, border: 'none', fontSize: '13px', fontWeight: 700, color: WHITE, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                {saving ? 'Cancelling...' : 'Yes, Cancel Campaign'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AdminBoosts() {
  const [boosts, setBoosts]         = useState([])
  const [loading, setLoading]       = useState(true)
  const [filter, setFilter]         = useState('all') // all | ending_today | pending | running | completed
  const [selectedBoost, setSelectedBoost] = useState(null)
  const [search, setSearch]         = useState('')
  const [stats, setStats]           = useState({ ending: 0, pending: 0, running: 0, completed: 0, revenue: 0 })

  useEffect(() => { fetchBoosts() }, [])

  const fetchBoosts = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('boosts')
      .select('*, sellers(shop_name, phone, country)')
      .order('created_at', { ascending: false })

    const enriched = (data || []).map(b => ({
      ...b,
      seller_name: b.sellers?.shop_name || 'Unknown',
      seller_phone: b.sellers?.phone || '',
    }))

    setBoosts(enriched)

    // Stats
    const now = Date.now()
    const ending = enriched.filter(b => b.status === 'running' && b.ends_at && new Date(b.ends_at).getTime() - now < 86400000).length
    const pending = enriched.filter(b => ['pending', 'paid', 'preparing'].includes(b.status)).length
    const running = enriched.filter(b => b.status === 'running').length
    const completed = enriched.filter(b => b.status === 'completed').length
    const revenue = enriched.filter(b => b.payment_status === 'paid').reduce((s, b) => s + (b.service_fee_amount || 0), 0)
    setStats({ ending, pending, running, completed, revenue })

    setLoading(false)
  }

  const filteredBoosts = boosts.filter(b => {
    const now = Date.now()
    const matchSearch = !search ||
      b.seller_name?.toLowerCase().includes(search.toLowerCase()) ||
      b.fb_campaign_name?.toLowerCase().includes(search.toLowerCase())

    if (!matchSearch) return false

    if (filter === 'ending_today') return b.status === 'running' && b.ends_at && new Date(b.ends_at).getTime() - now < 86400000
    if (filter === 'pending') return ['pending', 'paid', 'preparing'].includes(b.status)
    if (filter === 'running') return b.status === 'running'
    if (filter === 'completed') return b.status === 'completed' || b.status === 'cancelled'
    return true
  })

  // Sort — ending today first, then pending, then running, then rest
  const sorted = [...filteredBoosts].sort((a, b) => {
    const now = Date.now()
    const aEnding = a.status === 'running' && a.ends_at && new Date(a.ends_at).getTime() - now < 86400000
    const bEnding = b.status === 'running' && b.ends_at && new Date(b.ends_at).getTime() - now < 86400000
    if (aEnding && !bEnding) return -1
    if (!aEnding && bEnding) return 1
    const ORDER = { pending: 0, paid: 1, preparing: 2, running: 3, completed: 4, cancelled: 5 }
    return (ORDER[a.status] || 9) - (ORDER[b.status] || 9)
  })

  const FILTERS = [
    { key: 'all',          label: 'All',           count: boosts.length },
    { key: 'ending_today', label: '🔴 Ending Today', count: stats.ending },
    { key: 'pending',      label: '⏳ Pending',      count: stats.pending },
    { key: 'running',      label: '🟢 Running',      count: stats.running },
    { key: 'completed',    label: '✅ Completed',    count: stats.completed },
  ]

  if (loading) return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED }}>Loading...</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: BG, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
      <AdminNav />

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ padding: '24px 32px' }}>

          {/* Header */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '20px', fontWeight: 800, color: TEXT, letterSpacing: '-0.3px' }}>📢 AdBoost Queue</div>
            <div style={{ fontSize: '13px', color: MUTED, marginTop: '3px' }}>Social media manager operations dashboard</div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Ending Today', val: stats.ending, color: RED, bg: '#FEF2F2', urgent: stats.ending > 0 },
              { label: 'Pending Launch', val: stats.pending, color: AMBER, bg: '#FFFBEB' },
              { label: 'Running', val: stats.running, color: GREEN, bg: '#F0FDF4' },
              { label: 'Completed', val: stats.completed, color: MUTED, bg: BG },
              { label: 'Total Revenue (fees)', val: '৳' + Math.round(stats.revenue).toLocaleString(), color: BLUE, bg: '#EFF6FF' },
            ].map(s => (
              <div key={s.label} style={{ background: s.urgent ? s.bg : WHITE, borderRadius: '12px', border: `1px solid ${s.urgent ? '#FECACA' : BORDER}`, padding: '14px 16px' }}>
                <div style={{ fontSize: '18px', fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Search + Filters */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search seller or campaign name..."
              style={{ ...inp, maxWidth: '280px', flex: 1 }}
              onFocus={e => e.target.style.borderColor = BLUE} onBlur={e => e.target.style.borderColor = BORDER} />
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {FILTERS.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  style={{ padding: '7px 14px', borderRadius: '8px', border: filter === f.key ? 'none' : '1px solid ' + BORDER, fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: filter === f.key ? BLUE : WHITE, color: filter === f.key ? WHITE : MUTED, fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                  {f.label} {f.count > 0 && <span style={{ marginLeft: '4px', background: filter === f.key ? 'rgba(255,255,255,0.3)' : BG, padding: '1px 6px', borderRadius: '10px', fontSize: '10px' }}>{f.count}</span>}
                </button>
              ))}
            </div>
            <button onClick={fetchBoosts}
              style={{ padding: '7px 14px', borderRadius: '8px', background: BG, border: '1px solid ' + BORDER, fontSize: '12px', fontWeight: 600, color: MUTED, cursor: 'pointer', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
              🔄 Refresh
            </button>
          </div>

          {/* Queue Table */}
          <div style={{ background: WHITE, borderRadius: '14px', border: '1px solid ' + BORDER, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                <thead>
                  <tr style={{ background: BG }}>
                    {['Campaign', 'Seller', 'Type · Goal', 'Budget', 'Duration', 'Status', 'End Date', 'Action'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid ' + BORDER, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: MUTED, fontSize: '13px' }}>No campaigns found</td></tr>
                  ) : sorted.map(boost => {
                    const cfg = STATUS_CONFIG[boost.status] || STATUS_CONFIG.pending
                    const now = Date.now()
                    const isEnding = boost.status === 'running' && boost.ends_at && new Date(boost.ends_at).getTime() - now < 86400000
                    const rowBg = isEnding ? '#FFF5F5' : 'transparent'

                    return (
                      <tr key={boost.id} style={{ background: rowBg, borderBottom: '1px solid ' + BORDER }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT, maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{boost.fb_campaign_name || `#${boost.boost_number}`}</div>
                          <div style={{ fontSize: '10.5px', color: MUTED }}>{boost.market}</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: TEXT }}>{boost.seller_name}</div>
                          {boost.seller_phone && <div style={{ fontSize: '10.5px', color: MUTED }}>{boost.seller_phone}</div>}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontSize: '12px', color: TEXT }}>{BOOST_TYPE_LABELS[boost.boost_type]}</div>
                          <div style={{ fontSize: '10.5px', color: MUTED }}>{GOAL_LABELS[boost.goal]}</div>
                        </td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <div style={{ fontSize: '12.5px', fontWeight: 700, color: TEXT }}>৳{boost.budget_bdt?.toLocaleString()}</div>
                          <div style={{ fontSize: '10.5px', color: MUTED }}>${boost.budget_usd?.toFixed(2)}</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontSize: '12.5px', color: TEXT }}>{boost.duration_days}d</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 700, color: isEnding ? RED : cfg.color, background: isEnding ? '#FEF2F2' : cfg.bg, padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                            {isEnding ? '🔴 Ending Today' : cfg.label}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          {boost.ends_at
                            ? <div style={{ fontSize: '12px', color: isEnding ? RED : MUTED, fontWeight: isEnding ? 700 : 400 }}>{new Date(boost.ends_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
                            : <div style={{ fontSize: '12px', color: MUTED }}>—</div>}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <button onClick={() => setSelectedBoost(boost)}
                            style={{ padding: '6px 14px', borderRadius: '8px', background: isEnding ? RED : BLUE, border: 'none', fontSize: '12px', fontWeight: 700, color: WHITE, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
                            {boost.status === 'paid' || boost.status === 'preparing' ? '🚀 Launch' :
                             boost.status === 'running' ? (isEnding ? '⚠️ Complete' : '📋 View') :
                             boost.status === 'pending' ? '💳 Pending' :
                             '📋 View'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* P&L Summary */}
          {boosts.filter(b => b.status === 'completed').length > 0 && (
            <div style={{ marginTop: '20px' }}>
              <SectionCard title="P&L Summary" subtitle="Pass-through ad spend is not your revenue — only service fees are income">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                  {(() => {
                    const paid = boosts.filter(b => b.payment_status === 'paid')
                    const totalCollected = paid.reduce((s, b) => s + (b.total_payable || 0), 0)
                    const adSpend = paid.reduce((s, b) => s + (b.budget_bdt || 0), 0)
                    const serviceFees = paid.reduce((s, b) => s + (b.service_fee_amount || 0), 0)
                    const vat = paid.reduce((s, b) => s + (b.vat_amount || 0), 0)
                    const totalUSD = paid.reduce((s, b) => s + (b.budget_usd || 0), 0)
                    return [
                      { label: 'Total Collected (BDT)', val: '৳' + Math.round(totalCollected).toLocaleString(), color: TEXT },
                      { label: 'Ad Spend (Pass-through)', val: '৳' + Math.round(adSpend).toLocaleString(), color: MUTED, note: 'Not your income' },
                      { label: 'Service Fees (Your Revenue)', val: '৳' + Math.round(serviceFees).toLocaleString(), color: GREEN },
                      { label: 'VAT Collected', val: '৳' + Math.round(vat).toLocaleString(), color: AMBER, note: 'Payable to NBR' },
                      { label: 'Total USD Spent on Meta', val: '$' + totalUSD.toFixed(2), color: BLUE },
                    ]
                  })().map(s => (
                    <div key={s.label} style={{ background: BG, borderRadius: '10px', padding: '12px 14px' }}>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: s.color }}>{s.val}</div>
                      <div style={{ fontSize: '11px', color: MUTED, marginTop: '3px' }}>{s.label}</div>
                      {s.note && <div style={{ fontSize: '10px', color: AMBER, marginTop: '2px', fontWeight: 600 }}>{s.note}</div>}
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

        </div>
      </div>

      {/* Brief Modal */}
      {selectedBoost && (
        <BriefModal
          boost={selectedBoost}
          onClose={() => setSelectedBoost(null)}
          onLaunch={fetchBoosts}
          onComplete={fetchBoosts}
          onCancel={fetchBoosts}
        />
      )}
    </div>
  )
}